import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { useDatasetSessionStorageContext } from '../../../hooks/context/useDatasetSessionStorageContext';
import { getDataKey } from '../../../utils/getDataKey';
import { matchPath, useHistory, useLocation, useParams } from 'react-router';
import { GlobalLocationStateType } from '../../../types/types';
import useRoutes from '../../../hooks/useRoutes';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { useSelectedRowsContext } from '../../../hooks/context/useSelectedRowsContext';
import { getDatasetUrlParams } from '../../../utils/getDatasetUrlParams';
import { isFromPageReload } from '../../../utils/isFromPageReload';

type ResolveState = {
  isLoading: boolean;
  externalRow: any | undefined;
};

export const useExternalUrlDetailGuard = () => {
  const { fromItemExternalUrl, setDatasetSessionStorageState } =
    useDatasetSessionStorageContext();
  const { fetchDatasetData, dataset } = useDatasetContext();
  const { setExpandedRow } = useSelectedRowsContext();

  const { pathname, state } = useLocation<GlobalLocationStateType>();
  const history = useHistory();
  const routes = useRoutes();
  const { t } = useTranslate();
  const { datasetId, itemId: itemIdParam } = useParams<{
    datasetId?: string;
    itemId?: string;
  }>();
  const dataKey = getDataKey(dataset);

  // fromItemExternalUrl flips false mid-display (lastLoadedDataset write +
  // pathname change) and the subset-route switch remounts DatasetDetail — the
  // enteredFromExternalUrl router state keeps the external flow alive there.
  // It survives a full reload though, and reload is owned by
  // useDatasetUrlDetailReloadGuard (redirect to the list), so skip it here.
  const isExternalDisplay =
    (fromItemExternalUrl || Boolean(state?.enteredFromExternalUrl)) &&
    !isFromPageReload();

  const [resolveState, setResolveState] = useState<ResolveState>({
    isLoading: isExternalDisplay,
    externalRow: undefined,
  });

  const setDatasetIdToSession = () => {
    if (dataset?.datasetId) {
      setDatasetSessionStorageState({ lastLoadedDataset: dataset.datasetId });
    }
  };

  const fetchDataForExternalUrl = useCallback(async () => {
    // External URL with /detail/ form has no router state.itemId — fall back
    // to URL params so cold-start on /detail/X[/subset/Y] behaves like /item/.
    const resolvedItemId = state?.itemId ?? itemIdParam;

    // No external item url flow
    if (!isExternalDisplay || !resolvedItemId) {
      setResolveState({ isLoading: false, externalRow: undefined });
      setDatasetIdToSession();
      return;
    }

    // External url item flow
    setResolveState({ isLoading: true, externalRow: undefined });

    try {
      const data = await fetchDatasetData(false, undefined, [], {
        [dataKey]: { equals: resolvedItemId },
      });

      setDatasetIdToSession();

      // Id not found: probe came back empty. Toast and go back to the list with
      // a fresh initial batch (triggerInitData) instead of an empty detail.
      if (!data || data.length === 0) {
        toast(t('noRecordsDataset'));

        if (datasetId) {
          history.replace(
            routes.datasetList(datasetId),
            dataset?.filterFirst ? undefined : { triggerInitData: true }
          );
        }

        // Keep the spinner up so Detail never mounts with empty rows.
        return;
      }

      setResolveState({ isLoading: false, externalRow: data[0] });
    } catch (error) {
      setDatasetIdToSession();
      setResolveState({ isLoading: false, externalRow: undefined });

      // Rethrow error to be processed in parent
      if (error instanceof Error) {
        throw error;
      }
    }
  }, [dataset, fromItemExternalUrl, fetchDatasetData, pathname, itemIdParam]);

  //Trigger flow on initial render
  useEffect(() => {
    fetchDataForExternalUrl();
  }, []);

  // Handle dataset init load on exit
  useEffect(() => {
    const unblock = history.block((target, action) => {
      if (action === 'PUSH') {
        const match = matchPath(target.pathname, {
          path: routes.relative.datasetList(),
          exact: true,
          strict: false,
        });

        const { datasetId: targetDatasetId } = getDatasetUrlParams(
          target.pathname
        );

        // Modify state during redirect to another dataset page
        // Reload only when redirect on same dataset
        if (
          match &&
          datasetId === targetDatasetId &&
          isExternalDisplay &&
          !dataset?.filterFirst
        ) {
          setExpandedRow(null);
          target.state = {
            ...(target.state ?? {}),
            triggerInitData: true,
          };
        }
      }

      return undefined;
    });

    return unblock;
  }, [dataset]);

  return {
    isLoading: resolveState.isLoading,
    externalRow: resolveState.externalRow,
  };
};
